import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { isAuthenticated, login, logout, getCurrentUser, isLocalDev, refreshTokens, tokenExpiresIn } from '../lib/auth';

interface AuthContextValue {
  isLoggedIn: boolean;
  user: { sub: string; email: string; name: string; groups: string[] } | null;
  isAdmin: boolean;
  login: () => Promise<void>;
  logout: () => void;
  refreshAuth: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [isLoggedIn, setIsLoggedIn] = useState(() => isLocalDev() || isAuthenticated());
  const [user, setUser] = useState(() => getCurrentUser());
  const [isLoading, setIsLoading] = useState(!isLocalDev());

  useEffect(() => {
    setIsLoggedIn(isLocalDev() || isAuthenticated());
    setUser(getCurrentUser());
    setIsLoading(false);
  }, []);

  // Periodic token check — refresh before expiry or redirect to login
  useEffect(() => {
    if (isLocalDev()) return;

    const CHECK_INTERVAL = 60_000; // check every 60s
    const REFRESH_THRESHOLD = 300;  // refresh when <5 min left

    const timer = setInterval(async () => {
      const remaining = tokenExpiresIn();
      if (remaining > REFRESH_THRESHOLD) return; // still fresh

      const refreshed = await refreshTokens();
      if (refreshed) {
        setIsLoggedIn(true);
        setUser(getCurrentUser());
      } else {
        // Refresh failed — session expired, redirect to login
        setIsLoggedIn(false);
        setUser(null);
        login();
      }
    }, CHECK_INTERVAL);

    return () => clearInterval(timer);
  }, []);

  const doLogin = useCallback(async () => {
    await login();
  }, []);

  const doLogout = useCallback(() => {
    logout();
    setIsLoggedIn(false);
    setUser(null);
  }, []);

  const refreshAuth = useCallback(() => {
    setIsLoggedIn(isLocalDev() || isAuthenticated());
    setUser(getCurrentUser());
  }, []);

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, isAdmin: user?.groups?.includes('admin') ?? false, login: doLogin, logout: doLogout, refreshAuth, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
